import { type FC } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { type CrossSellProduct } from '@redteclab/api/clients/retail-media-xsell-service'

import { Link } from 'base-ui'

import { useOpenableControls } from '../../../../generic-hooks'
import { useGlobalConfigContext } from '../../../../global-config'
import { priceGetFormattedPrice } from '../../../../price'
import { TosSuperscript } from '../../../../tos-superscripts'
import { priceAccessorGetParsedPrice } from '../../../model/priceAccessor/priceAccessorGetParsedPrice'
import { ProductDeliveryInfoDialog } from '../../ProductDeliveryInfoDialog'
import { useProductPageContext } from '../../productPageContext'

const calculateDiscountPercents = ({
  displayPrice,
  locale,
  strikedThrouthPrice,
}: {
  displayPrice: number
  locale: string
  strikedThrouthPrice: number
}): string | undefined => {
  const rounded = 100 - (displayPrice / strikedThrouthPrice) * 100
  if (rounded > 0) {
    return rounded.toLocaleString(locale, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    })
  }

  return undefined
}

type ProductCrossSellBuyTogetherPricesProps = {
  recommendedProduct: CrossSellProduct
}

export const ProductCrossSellBuyTogetherPrices: FC<
  ProductCrossSellBuyTogetherPricesProps
> = ({ recommendedProduct }) => {
  const intl = useIntl()

  const { handleClose, handleOpen, isOpen } = useOpenableControls(false)
  const { locale, superScripts } = useGlobalConfigContext()
  const { currentOffer } = useProductPageContext()

  const recommandedDisplayPrice = priceAccessorGetParsedPrice(
    recommendedProduct.price,
  )
  const recommandedStrikedThrouthPrice = priceAccessorGetParsedPrice(
    recommendedProduct.oldprice,
  )

  const displayPrice =
    (currentOffer?.offerPrice?.price ?? 0) +
    (recommandedDisplayPrice?.amount ?? 0)

  const strikedThrouthPrice =
    (currentOffer?.strikeThroughOfferPrice?.price ?? 0) +
    (recommandedStrikedThrouthPrice?.amount ?? 0)

  const discountPercent = calculateDiscountPercents({
    displayPrice,
    locale,
    strikedThrouthPrice,
  })

  const isDiscountedPrice = strikedThrouthPrice > displayPrice

  return (
    <div className="flex flex-col items-center">
      {isDiscountedPrice ? (
        <div className="self-start text-xs text-dark-primary-low">
          <FormattedMessage id="general.label.listPrice" />
          <TosSuperscript value={superScripts.avp} />
        </div>
      ) : null}
      <div className="flex">
        {isDiscountedPrice ? (
          <div className="mr-3.5 text-xl text-dark-primary-medium line-through">
            {priceGetFormattedPrice(strikedThrouthPrice, intl)}
          </div>
        ) : null}
        <span className="text-xl font-bold text-dark-accent">
          {priceGetFormattedPrice(displayPrice, intl)}
        </span>
      </div>
      <div className="inline-flex items-center text-xs">
        <FormattedMessage id="general.label.vatExclShipping" />
        <Link
          className="ml-1"
          onClick={handleOpen}
          slots={{ root: 'button' }}
          type="button"
        >
          <FormattedMessage id="general.label.shipping" />
        </Link>
      </div>
      {isDiscountedPrice && discountPercent ? (
        <div className="font-bold">
          <FormattedMessage
            id="general.label.savings"
            values={{
              discountPercent,
            }}
          />
        </div>
      ) : null}
      <ProductDeliveryInfoDialog isOpen={isOpen} onClose={handleClose} />
    </div>
  )
}
